import { sha256 as noble_sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import { DTO__base } from '@shared/common/DTO__base';
import { PROPS__crypto, PROPS__crypto_default } from './PROPS__crypto';

export namespace UTILS__crypto {
  export namespace base {
    export function key_with_prefix(key: string, prefix: string) {
      return prefix ? `${prefix}.${key}` : key;
    }

    export function to_flat_object<T extends object>(value: T, prefix: string, result: Record<string, string> = {}, excluded_keys: Set<string> = new Set()) {
      let entries = Object.entries(value);

      for (const [key, val] of entries) {
        if (excluded_keys.has(key)) continue;

        const key_new = key_with_prefix(key, prefix);
        flatten_and_filter(val, key_new, result);
      }
    }

    export function flatten_and_filter(value: any, prefix: string = '', result: Record<string, string> = {}): Record<string, string> {
      if (value instanceof DTO__base) {
        if (value.is_stub()) return result;

        value.get_flat_object_for_hash(prefix, result);
      } else if (Array.isArray(value)) {
        for (const [index, item] of value.entries()) {
          const key_new = key_with_prefix(`${index}`, prefix);
          flatten_and_filter(item, key_new, result);
        }
      } else if (value === undefined) {
        //do nothing
      } else if (value === null) {
        //do nothing
      } else if (value === '') {
        //do nothing
      } else if (typeof value === 'object') {
        to_flat_object(value, prefix, result);
      } else {
        result[prefix] = value;
      }

      return result;
    }

    export function object_to_string(target: object, props_params: Partial<PROPS__crypto> = PROPS__crypto_default): string {
      const props: PROPS__crypto = { ...PROPS__crypto_default, ...props_params };

      const flat_map = flatten_and_filter(target);

      // Step 2: Get the flat keys and apply 'except_fields' filter
      let keys = Object.keys(flat_map);

      if (props.except_fields.length > 0) {
        // This removes any key that matches or starts with an excluded field
        keys = keys.filter(key => !props.except_fields.includes(key));
      }

      // Step 3: Sort keys alphabetically for determinism
      const keys_sorted = keys.sort();

      // Step 4: Join into the final string format
      // Note: flat_map[key] is already a stringified primitive from flatten_and_filter
      const out = keys_sorted.map(key => `${key}=${flat_map[key]}`).join('|');

      return out;
    }
  }
  /*
   * CRC32
   */
  export namespace crc32 {
    export function from_object(target: object, props_params: Partial<PROPS__crypto> = PROPS__crypto_default) {
      let props: PROPS__crypto = { ...PROPS__crypto_default, ...props_params };

      let target_string = UTILS__crypto.base.object_to_string(target, props);

      return UTILS__crypto.crc32.from_string(target_string, props);
    }

    export function from_string(string: string, props_params: Partial<PROPS__crypto> = PROPS__crypto_default) {
      let props: PROPS__crypto = { ...PROPS__crypto_default, ...props_params };

      string += props.salt;

      const table = new Uint32Array(256);
      const poly = 0xedb88320;

      // Precompute the CRC32 table
      for (let i = 0; i < 256; i++) {
        let crc = i;
        for (let j = 8; j > 0; j--) {
          crc = crc & 1 ? poly ^ (crc >>> 1) : crc >>> 1;
        }
        table[i] = crc;
      }

      let crc = 0xffffffff;
      for (let i = 0; i < string.length; i++) {
        const byte = string.charCodeAt(i) & 0xff;
        crc = (crc >>> 8) ^ table[(crc & 0xff) ^ byte];
      }

      let hash = crc ^ 0xffffffff;

      return props.with_protocol ? `crc32:${hash}` : `${hash}`;
    }
  }

  /*
   * DJB2
   */

  export namespace djb2 {
    export function from_string(value: string): number {
      let out = 0;

      for (let i = 0; i < value.length; i++) {
        out = (out << 5) - out + value.charCodeAt(i);
        out |= 0; // Convert to 32bit integer
      }

      return out;
    }
  }

  /*
   * SHA256
   */
  export namespace sha256 {
    export function from_uint8_array(blob: Uint8Array, props_params: Partial<PROPS__crypto> = PROPS__crypto_default) {
      let props: PROPS__crypto = { ...PROPS__crypto_default, ...props_params };

      let blob_to_hash: Uint8Array;

      if (props.salt) {
        const encoder = new TextEncoder();
        const blob_salt = encoder.encode(props.salt);

        // Combine blob + saltBytes into one Uint8Array
        blob_to_hash = new Uint8Array(blob.length + blob_salt.length);
        blob_to_hash.set(blob);
        blob_to_hash.set(blob_salt, blob.length);
      } else {
        blob_to_hash = blob;
      }

      const hash_bytes = noble_sha256(blob_to_hash);

      const hash = bytesToHex(hash_bytes);

      return props.with_protocol ? `sha256:${hash}` : hash;
    }

    export function from_object(target: object, props_params: Partial<PROPS__crypto> = PROPS__crypto_default) {
      let props: PROPS__crypto = { ...PROPS__crypto_default, ...props_params };

      let target_string = UTILS__crypto.base.object_to_string(target, props);

      return UTILS__crypto.sha256.from_string(target_string, props);
    }

    export function from_string(value: string, props_params: Partial<PROPS__crypto> = PROPS__crypto_default) {
      let props: PROPS__crypto = { ...PROPS__crypto_default, ...props_params };

      value += props.salt;

      const encoder = new TextEncoder();
      const input_bytes = encoder.encode(value);

      // 3. Perform the synchronous hash
      const hash_bytes = noble_sha256(input_bytes);

      const hash = bytesToHex(hash_bytes);

      return props.with_protocol ? `sha256:${hash}` : hash;
    }
  }
}
